import React, { useMemo } from 'react';
import PanelLayout from '../PanelLayout';
import {
  Alert,
  Box,
  Skeleton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import StructureListItem from '../../../../StructureListItem';
import useCommanderOffline from '../../../../../../../../../service/device/CommanderAPI/hooks/useCommanderOffline';
import useTypeRequest from '../../../useTypeRequest';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../../../../../../../service/store/Store.constants';
import useTypeEntity from '../../../../useTypeEntity';
import useStructures from '../../../../../../../../../redux/structure/hooks/useStructures';
import { getGroupsFromDeviceIdsGroupDictionary } from '../../../../../../../../../redux/structure/selectors/Group.selector';
import { useTranslation } from 'react-i18next';
import useWatchError from '../../../../../../../../../shared/hooks/useWatchError';
import { CBoxOfflineIcon } from '../../../../../../../../../assets/dashboard';
import { toBase64 } from '../../../../../../../../../shared/util/base64';
import { BusinessLayerGroupType } from '../../../../../../../../../redux/structure/util/getTypeFromGroup';
import { useGetTypeFromGroup } from '../../../../../../../../../redux/structure/util/useGetTypeFromGroup';

function CommanderOfflineTableList() {
  const { groupId, marketId } = useTypeEntity({
    pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
  });
  const { t } = useTranslation();
  const getTypeFromGroup = useGetTypeFromGroup(marketId);
  const typeRequest = useTypeRequest(marketId, groupId);
  const {
    data: dataCommanderOffline,
    isLoading: isLoadingDataCommander,
    error: errorCommanderOffline,
  } = useCommanderOffline({
    groupId,
    workspaceId: marketId,
    typeRequest,
  });

  const {
    data: structuresData,
    isLoading: isLoadingStructures,
    error: errorStructures,
    originalArgs: structuresOriginalArgs,
  } = useStructures({
    typeRequest,
    groupId,
    workspaceId: marketId,
  });

  const childGroups = useMemo(
    () =>
      structuresData?.flatMap((structure, i) =>
        (groupId
          ? structure.entities?.groups[toBase64(groupId)]?.childGroups ?? []
          : structure.result
        )
          .flatMap((groupId) => {
            const currentWorkspaceId = structuresOriginalArgs[i] ?? marketId;
            const groupsWithDeviceError = getGroupsFromDeviceIdsGroupDictionary(
              structure.entities?.groups ?? {},
              dataCommanderOffline?.map((device) => device.deviceID) ?? [],
              groupId
            );
            return groupsWithDeviceError.filter(
              (group) =>
                getTypeFromGroup(group, currentWorkspaceId) ===
                BusinessLayerGroupType.Store
            );
          })
          .map((group) => ({
            ...group,
            workspaceId: structuresOriginalArgs[i] ?? marketId,
          }))
      ),

    [
      dataCommanderOffline,
      getTypeFromGroup,
      groupId,
      marketId,
      structuresData,
      structuresOriginalArgs,
    ]
  );
  const loading = isLoadingDataCommander || isLoadingStructures;
  const error = errorCommanderOffline || errorStructures;

  useWatchError(error);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const childGroupsPaginated = useMemo(
    () =>
      childGroups?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ) ?? [],
    [childGroups, page, rowsPerPage]
  );

  const content = loading ? (
    <Skeleton variant="rectangular" width={'100%'} height={200} />
  ) : (
    <Box mt={2}>
      <Typography variant="body1" gutterBottom>
        {t('dashboard.commander_offline.description_list')}
      </Typography>
      {childGroupsPaginated.length === 0 ? (
        <Alert severity="info">
          {t('dashboard.commander_offline.no_stores')}
        </Alert>
      ) : (
        <>
          <Stack spacing={1} mt={2}>
            {childGroupsPaginated.map((entity: any) => (
              <StructureListItem key={entity?.id} entity={entity} />
            ))}
          </Stack>
          <TablePagination
            component="div"
            count={childGroups?.length ?? 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
  return (
    <PanelLayout
      innerContainerMaxWidth={'xl'}
      subPages={[
        {
          name: t('dashboard.commander_offline.title'),
          url: '',
        },
      ]}
      title={t('dashboard.commander_offline.title')}
      icon={<CBoxOfflineIcon />}
    >
      {content}
    </PanelLayout>
  );
}

export default CommanderOfflineTableList;
